/**
 * خدمات الميزات التشغيلية والرقابية المتقدمة:
 * 1) الختم المائي التلقائي على صور الإثبات (التاريخ + الوقت + الفرع + اسم الموظف)
 * 2) شات الموظفين والمشرفين الداخلي (خاص + قنوات الفروع + تسجيلات صوتية + وسائط)
 * 3) التعليمات والبيانات الإدارية مع إثبات القراءة والاطلاع (قرأت وألتزم)
 * 4) التعليقات والملاحظات الصوتية داخل المهام
 * 5) تقارير تسليم الورديات (Shift Handover)
 * 6) مركز الملفات والوسائط الشامل (الحذف اليدوي والتلقائي الانتقائي لتوفير المساحة)
 * 7) الإغلاق التلقائي للجولات الساعية الفائتة
 * 8) تشخيص التخزين السحابي والمحلي والنسخ الاحتياطي وتصدير البيانات
 */

import type { DataGateway } from '@/data/types';
import type { Attachment, AttachmentKind, Branch, Task } from '@/domain/types';
import type { MediaHubRetentionPolicy } from '@/domain/settings';
import { newId } from '@/domain/ids';
import { deleteAttachment, uploadAttachment } from './attachment-service';
import type { Actor } from './org-service';

export const META_PREFIX = {
  chatMessage: 'tf-meta://chat-msg',
  bulletin: 'tf-meta://bulletin',
  bulletinAck: 'tf-meta://bulletin-ack',
  taskComment: 'tf-meta://task-comment',
  shiftHandover: 'tf-meta://shift-handover',
} as const;

/* ============================================================================
 * 1) الختم المائي التلقائي على صور الإثبات (Photo Watermark)
 * ========================================================================== */

export interface WatermarkOptions {
  companyName: string;
  branchName?: string | null;
  employeeName: string;
  timeZone?: string;
}

export async function applyPhotoWatermark(file: File, options: WatermarkOptions): Promise<File> {
  if (typeof document === 'undefined' || !file.type.startsWith('image/')) {
    return file;
  }
  return new Promise<File>((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        URL.revokeObjectURL(objectUrl);
        const maxDim = 1600;
        let width = img.naturalWidth || img.width || 1000;
        let height = img.naturalHeight || img.height || 750;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(file);
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        const fontSize = Math.max(14, Math.round(width * 0.022));
        const padding = Math.max(12, Math.round(fontSize * 0.8));
        const barHeight = fontSize * 2.8 + padding * 1.4;

        const gradient = ctx.createLinearGradient(0, height - barHeight - 20, 0, height);
        gradient.addColorStop(0, 'rgba(9, 13, 22, 0)');
        gradient.addColorStop(0.35, 'rgba(9, 13, 22, 0.72)');
        gradient.addColorStop(1, 'rgba(9, 13, 22, 0.9)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, height - barHeight - 20, width, barHeight + 20);

        const nowStr = new Intl.DateTimeFormat('ar-EG', {
          dateStyle: 'medium',
          timeStyle: 'medium',
          timeZone: options.timeZone || 'Asia/Riyadh',
        }).format(new Date());

        const locationLabel = options.branchName
          ? `${options.companyName} — ${options.branchName}`
          : options.companyName;

        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'right';
        ctx.font = `bold ${fontSize}px Tahoma, "Segoe UI", sans-serif`;
        ctx.fillText(`📍 ${locationLabel}  |  👤 ${options.employeeName}`, width - padding, height - padding - fontSize * 1.25);

        ctx.fillStyle = '#93c5fd';
        ctx.font = `${Math.round(fontSize * 0.92)}px Tahoma, "Segoe UI", sans-serif`;
        ctx.fillText(`🕒 التوقيت الموثّق: ${nowStr}  •  TaskFlow Verified`, width - padding, height - padding);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            const baseName = file.name.replace(/\.[^.]+$/, '');
            const watermarked = new File([blob], `${baseName}-verified.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(watermarked);
          },
          'image/jpeg',
          0.85,
        );
      } catch {
        resolve(file);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
    img.src = objectUrl;
  });
}

/* ============================================================================
 * 2) شات الموظفين والمشرفين الداخلي (Internal Staff & Supervisor Chat)
 * ========================================================================== */

export interface ChatMessageRecord {
  id: string;
  companyId: string;
  channelId: string; // 'company:general' | 'branch:<id>' | 'dm:<u1>_<u2>'
  senderUserId: string;
  senderMembershipId: string | null;
  senderName: string;
  senderRoleLabel: string;
  text: string;
  mediaAttachmentId: string | null;
  mediaFileName: string | null;
  mediaMimeType: string | null;
  mediaKind: 'image' | 'video' | 'audio' | 'document' | null;
  createdAt: string;
}

export function buildDmChannelId(userA: string, userB: string): string {
  const sorted = [userA, userB].sort();
  return `dm:${sorted[0]}_${sorted[1]}`;
}

export function classifyMimeType(mimeType: string): 'image' | 'video' | 'audio' | 'document' {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'document';
}

export function attachmentKindForMime(mimeType: string): AttachmentKind {
  return mimeType.startsWith('image/') ? 'image' : 'document';
}

export async function listChatMessages(
  gateway: DataGateway,
  companyId: string,
  channelId?: string,
): Promise<ChatMessageRecord[]> {
  const rows = await gateway.table('attachments').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'asc' }],
  });

  const messages: ChatMessageRecord[] = [];
  for (const row of rows) {
    if (!row.storagePath.startsWith(META_PREFIX.chatMessage) || !row.checksum) continue;
    try {
      const parsed = JSON.parse(row.checksum) as Omit<ChatMessageRecord, 'id' | 'companyId' | 'createdAt'>;
      if (channelId && parsed.channelId !== channelId) continue;
      messages.push({
        ...parsed,
        id: row.id,
        companyId: row.companyId,
        createdAt: row.createdAt,
      });
    } catch {
      // تجاهل السجلات التالفة
    }
  }
  return messages;
}

export async function sendChatMessage(
  gateway: DataGateway,
  actor: Actor,
  input: {
    channelId: string;
    senderName: string;
    senderRoleLabel: string;
    text: string;
    file?: File | null;
  },
): Promise<ChatMessageRecord> {
  let mediaAttachmentId: string | null = null;
  let mediaFileName: string | null = null;
  let mediaMimeType: string | null = null;
  let mediaKind: ChatMessageRecord['mediaKind'] = null;

  if (input.file) {
    const uploaded = await uploadAttachment(gateway, actor, {
      companyId: actor.companyId,
      taskId: null,
      file: input.file,
      kind: attachmentKindForMime(input.file.type),
      clientRef: `chat-media:${input.channelId}:${newId()}`,
    });
    mediaAttachmentId = uploaded.id;
    mediaFileName = uploaded.fileName;
    mediaMimeType = uploaded.mimeType;
    mediaKind = classifyMimeType(uploaded.mimeType);
  }

  const now = new Date().toISOString();
  const payload: Omit<ChatMessageRecord, 'id' | 'companyId' | 'createdAt'> = {
    channelId: input.channelId,
    senderUserId: actor.userId,
    senderMembershipId: actor.membershipId ?? null,
    senderName: input.senderName,
    senderRoleLabel: input.senderRoleLabel,
    text: input.text.trim(),
    mediaAttachmentId,
    mediaFileName,
    mediaMimeType,
    mediaKind,
  };

  const id = newId();
  const row = await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${META_PREFIX.chatMessage}/${input.channelId}/${id}`,
    fileName: `chat-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(payload),
    clientRef: `tf-chat:${id}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });

  return {
    ...payload,
    id: row.id,
    companyId: row.companyId,
    createdAt: row.createdAt,
  };
}

export async function deleteChatMessage(
  gateway: DataGateway,
  actor: Actor,
  message: ChatMessageRecord,
): Promise<void> {
  if (message.mediaAttachmentId) {
    await deleteAttachment(gateway, actor, message.mediaAttachmentId).catch(() => undefined);
  }
  const now = new Date().toISOString();
  await gateway.table('attachments').update(message.id, {
    deletedAt: now,
    uploadState: 'deleted',
    updatedAt: now,
  });
}

export function getChatLastReadTimestamp(channelId: string, userId: string): string {
  try {
    const primary = localStorage.getItem(`tf_chat_read_${userId}_${channelId}`);
    if (primary) return primary;
    if (channelId.startsWith('dm:')) {
      const raw = channelId.slice(3);
      const parts = raw.includes('_') ? raw.split('_') : raw.split(':');
      if (parts.length >= 2 && parts[0] && parts[1]) {
        const alt1 = `dm:${parts[1]}_${parts[0]}`;
        const alt2 = `dm:${parts[0]}:${parts[1]}`;
        const alt3 = `dm:${parts[1]}:${parts[0]}`;
        return (
          localStorage.getItem(`tf_chat_read_${userId}_${alt1}`) ||
          localStorage.getItem(`tf_chat_read_${userId}_${alt2}`) ||
          localStorage.getItem(`tf_chat_read_${userId}_${alt3}`) ||
          '1970-01-01T00:00:00.000Z'
        );
      }
    }
    return '1970-01-01T00:00:00.000Z';
  } catch {
    return '1970-01-01T00:00:00.000Z';
  }
}

export function markChatChannelRead(channelId: string, userId: string): void {
  try {
    const now = new Date().toISOString();
    localStorage.setItem(`tf_chat_read_${userId}_${channelId}`, now);
    if (channelId.startsWith('dm:')) {
      const raw = channelId.slice(3);
      const parts = raw.includes('_') ? raw.split('_') : raw.split(':');
      if (parts.length >= 2 && parts[0] && parts[1]) {
        localStorage.setItem(`tf_chat_read_${userId}_dm:${parts[1]}_${parts[0]}`, now);
        localStorage.setItem(`tf_chat_read_${userId}_dm:${parts[0]}:${parts[1]}`, now);
        localStorage.setItem(`tf_chat_read_${userId}_dm:${parts[1]}:${parts[0]}`, now);
      }
    }
  } catch {
    // ignore
  }
}

export async function countUnreadChatMessages(
  gateway: DataGateway,
  companyId: string,
  userId: string,
  branchId?: string | null,
): Promise<{ total: number; dms: number; channels: number }> {
  try {
    const messages = await listChatMessages(gateway, companyId);
    let dms = 0;
    let channels = 0;
    for (const msg of messages) {
      if (msg.senderUserId === userId) continue;
      const lastRead = getChatLastReadTimestamp(msg.channelId, userId);
      if (msg.createdAt <= lastRead) continue;

      if (msg.channelId.startsWith('dm:')) {
        const raw = msg.channelId.slice(3);
        const parts = raw.includes('_') ? raw.split('_') : raw.split(':');
        if (parts.includes(userId)) {
          dms += 1;
        }
      } else if (
        msg.channelId === 'company:general' ||
        (branchId && msg.channelId === `branch:${branchId}`)
      ) {
        channels += 1;
      }
    }
    return { total: dms + channels, dms, channels };
  } catch {
    return { total: 0, dms: 0, channels: 0 };
  }
}

/* ============================================================================
 * 3) تبويبة التعليمات والبيانات الإدارية (Bulletins & Official Instructions)
 * ========================================================================== */

export type BulletinPriority = 'normal' | 'important' | 'urgent';

export interface BulletinAckRecord {
  id: string;
  bulletinId: string;
  userId: string;
  membershipId: string | null;
  userName: string;
  branchId: string | null;
  acknowledgedAt: string;
}

export interface BulletinRecord {
  id: string;
  companyId: string;
  title: string;
  body: string;
  categoryLabel: string; // 'قرار إداري' | 'تعليمات تشغيل' | 'تعميم داخلي' | 'تنبيه سلامة وجودة'
  priority: BulletinPriority;
  pinned: boolean;
  branchId: string | null; // null = كل الفروع
  branchName: string | null;
  requiresAck: boolean;
  attachmentId: string | null;
  attachmentName: string | null;
  attachmentMimeType: string | null;
  authorUserId: string;
  authorName: string;
  authorRoleLabel: string;
  createdAt: string;
  acknowledgments: BulletinAckRecord[];
}

export async function listBulletins(
  gateway: DataGateway,
  companyId: string,
): Promise<BulletinRecord[]> {
  const rows = await gateway.table('attachments').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'desc' }],
  });

  const acksByBulletin = new Map<string, BulletinAckRecord[]>();
  for (const row of rows) {
    if (!row.storagePath.startsWith(META_PREFIX.bulletinAck) || !row.checksum) continue;
    try {
      const ack = JSON.parse(row.checksum) as Omit<BulletinAckRecord, 'id' | 'acknowledgedAt'>;
      const list = acksByBulletin.get(ack.bulletinId) ?? [];
      if (!list.some((item) => item.userId === ack.userId)) {
        list.push({
          ...ack,
          id: row.id,
          acknowledgedAt: row.createdAt,
        });
      }
      acksByBulletin.set(ack.bulletinId, list);
    } catch {
      // تجاهل
    }
  }

  const bulletins: BulletinRecord[] = [];
  for (const row of rows) {
    if (!row.storagePath.startsWith(`${META_PREFIX.bulletin}/`) || !row.checksum) continue;
    try {
      const parsed = JSON.parse(row.checksum) as Omit<
        BulletinRecord,
        'id' | 'companyId' | 'createdAt' | 'acknowledgments'
      >;
      bulletins.push({
        ...parsed,
        id: row.id,
        companyId: row.companyId,
        createdAt: row.createdAt,
        acknowledgments: acksByBulletin.get(row.id) ?? [],
      });
    } catch {
      // تجاهل
    }
  }

  return bulletins.sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

export async function createBulletin(
  gateway: DataGateway,
  actor: Actor,
  input: {
    title: string;
    body: string;
    categoryLabel: string;
    priority: BulletinPriority;
    pinned: boolean;
    branchId: string | null;
    branchName: string | null;
    requiresAck: boolean;
    authorName: string;
    authorRoleLabel: string;
    file?: File | null;
  },
): Promise<BulletinRecord> {
  let attachmentId: string | null = null;
  let attachmentName: string | null = null;
  let attachmentMimeType: string | null = null;

  if (input.file) {
    const uploaded = await uploadAttachment(gateway, actor, {
      companyId: actor.companyId,
      taskId: null,
      file: input.file,
      kind: attachmentKindForMime(input.file.type),
      clientRef: `bulletin-media:${newId()}`,
    });
    attachmentId = uploaded.id;
    attachmentName = uploaded.fileName;
    attachmentMimeType = uploaded.mimeType;
  }

  const id = newId();
  const now = new Date().toISOString();
  const payload = {
    title: input.title.trim(),
    body: input.body.trim(),
    categoryLabel: input.categoryLabel,
    priority: input.priority,
    pinned: input.pinned,
    branchId: input.branchId,
    branchName: input.branchName,
    requiresAck: input.requiresAck,
    attachmentId,
    attachmentName,
    attachmentMimeType,
    authorUserId: actor.userId,
    authorName: input.authorName,
    authorRoleLabel: input.authorRoleLabel,
  };

  const row = await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${META_PREFIX.bulletin}/${id}`,
    fileName: `bulletin-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(payload),
    clientRef: `tf-bulletin:${id}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });

  return {
    ...payload,
    id: row.id,
    companyId: row.companyId,
    createdAt: row.createdAt,
    acknowledgments: [],
  };
}

export async function acknowledgeBulletin(
  gateway: DataGateway,
  actor: Actor,
  input: {
    bulletinId: string;
    userName: string;
    branchId: string | null;
  },
): Promise<void> {
  const id = newId();
  const now = new Date().toISOString();
  const payload = {
    bulletinId: input.bulletinId,
    userId: actor.userId,
    membershipId: actor.membershipId ?? null,
    userName: input.userName,
    branchId: input.branchId,
  };
  await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${META_PREFIX.bulletinAck}/${input.bulletinId}/${actor.userId}`,
    fileName: `ack-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(payload),
    clientRef: `tf-ack:${input.bulletinId}:${actor.userId}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });
}

export async function deleteBulletin(
  gateway: DataGateway,
  actor: Actor,
  bulletin: BulletinRecord,
): Promise<void> {
  if (bulletin.attachmentId) {
    await deleteAttachment(gateway, actor, bulletin.attachmentId).catch(() => undefined);
  }
  const now = new Date().toISOString();
  await gateway.table('attachments').update(bulletin.id, {
    deletedAt: now,
    uploadState: 'deleted',
    updatedAt: now,
  });
}

/* ============================================================================
 * 4) التعليقات والملاحظات الصوتية داخل المهمة (Task Comments & Voice Notes)
 * ========================================================================== */

export interface TaskCommentRecord {
  id: string;
  companyId: string;
  taskId: string;
  senderUserId: string;
  senderName: string;
  senderRoleLabel: string;
  text: string;
  mediaAttachmentId: string | null;
  mediaFileName: string | null;
  mediaMimeType: string | null;
  mediaKind: 'image' | 'video' | 'audio' | 'document' | null;
  createdAt: string;
}

export async function listTaskComments(
  gateway: DataGateway,
  companyId: string,
  taskId: string,
): Promise<TaskCommentRecord[]> {
  const rows = await gateway.table('attachments').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'asc' }],
  });

  const prefix = `${META_PREFIX.taskComment}/${taskId}/`;
  const comments: TaskCommentRecord[] = [];
  for (const row of rows) {
    if (!row.storagePath.startsWith(prefix) || !row.checksum) continue;
    try {
      const parsed = JSON.parse(row.checksum) as Omit<TaskCommentRecord, 'id' | 'companyId' | 'createdAt'>;
      comments.push({
        ...parsed,
        id: row.id,
        companyId: row.companyId,
        createdAt: row.createdAt,
      });
    } catch {
      // تجاهل
    }
  }
  return comments;
}

export async function addTaskComment(
  gateway: DataGateway,
  actor: Actor,
  input: {
    taskId: string;
    senderName: string;
    senderRoleLabel: string;
    text: string;
    file?: File | null;
  },
): Promise<TaskCommentRecord> {
  let mediaAttachmentId: string | null = null;
  let mediaFileName: string | null = null;
  let mediaMimeType: string | null = null;
  let mediaKind: TaskCommentRecord['mediaKind'] = null;

  if (input.file) {
    const uploaded = await uploadAttachment(gateway, actor, {
      companyId: actor.companyId,
      taskId: null,
      file: input.file,
      kind: attachmentKindForMime(input.file.type),
      clientRef: `comment-media:${input.taskId}:${newId()}`,
    });
    mediaAttachmentId = uploaded.id;
    mediaFileName = uploaded.fileName;
    mediaMimeType = uploaded.mimeType;
    mediaKind = classifyMimeType(uploaded.mimeType);
  }

  const id = newId();
  const now = new Date().toISOString();
  const payload: Omit<TaskCommentRecord, 'id' | 'companyId' | 'createdAt'> = {
    taskId: input.taskId,
    senderUserId: actor.userId,
    senderName: input.senderName,
    senderRoleLabel: input.senderRoleLabel,
    text: input.text.trim(),
    mediaAttachmentId,
    mediaFileName,
    mediaMimeType,
    mediaKind,
  };

  const row = await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${META_PREFIX.taskComment}/${input.taskId}/${id}`,
    fileName: `comment-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(payload),
    clientRef: `tf-comment:${id}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });

  return {
    ...payload,
    id: row.id,
    companyId: row.companyId,
    createdAt: row.createdAt,
  };
}

export async function deleteTaskComment(
  gateway: DataGateway,
  actor: Actor,
  comment: TaskCommentRecord,
): Promise<void> {
  if (comment.mediaAttachmentId) {
    await deleteAttachment(gateway, actor, comment.mediaAttachmentId).catch(() => undefined);
  }
  const now = new Date().toISOString();
  await gateway.table('attachments').update(comment.id, {
    deletedAt: now,
    uploadState: 'deleted',
    updatedAt: now,
  });
}

/* ============================================================================
 * 5) تقارير تسليم الوردية (Shift Handover Reports)
 * ========================================================================== */

export interface ShiftHandoverRecord {
  id: string;
  companyId: string;
  branchId: string | null;
  branchName: string;
  shiftLabel: string;
  completedCount: number;
  pendingCount: number;
  overdueCount: number;
  summaryNotes: string;
  urgentIssues: string;
  nextShiftNotes: string;
  authorUserId: string;
  authorName: string;
  createdAt: string;
}

export async function listShiftHandovers(
  gateway: DataGateway,
  companyId: string,
): Promise<ShiftHandoverRecord[]> {
  const rows = await gateway.table('attachments').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'desc' }],
  });

  const handovers: ShiftHandoverRecord[] = [];
  for (const row of rows) {
    if (!row.storagePath.startsWith(META_PREFIX.shiftHandover) || !row.checksum) continue;
    try {
      const parsed = JSON.parse(row.checksum) as Omit<ShiftHandoverRecord, 'id' | 'companyId' | 'createdAt'>;
      handovers.push({
        ...parsed,
        id: row.id,
        companyId: row.companyId,
        createdAt: row.createdAt,
      });
    } catch {
      // تجاهل
    }
  }
  return handovers;
}

export async function createShiftHandover(
  gateway: DataGateway,
  actor: Actor,
  input: Omit<ShiftHandoverRecord, 'id' | 'companyId' | 'createdAt' | 'authorUserId'>,
): Promise<ShiftHandoverRecord> {
  const id = newId();
  const now = new Date().toISOString();
  const payload = {
    ...input,
    authorUserId: actor.userId,
  };
  const row = await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${META_PREFIX.shiftHandover}/${id}`,
    fileName: `handover-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(payload),
    clientRef: `tf-handover:${id}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });

  return {
    ...payload,
    id: row.id,
    companyId: row.companyId,
    createdAt: row.createdAt,
  };
}

export async function deleteShiftHandover(
  gateway: DataGateway,
  handoverId: string,
): Promise<void> {
  const now = new Date().toISOString();
  await gateway.table('attachments').update(handoverId, {
    deletedAt: now,
    uploadState: 'deleted',
    updatedAt: now,
  });
}

/* ============================================================================
 * 6) قسم الملفات والوسائط الشامل (Media & Files Hub + Selective Auto-Retention)
 * ========================================================================== */

export type MediaSourceCategory = 'task_proof' | 'chat_media' | 'bulletin_media' | 'branding';
export type MediaFileType = 'image' | 'video' | 'audio' | 'document';

export interface MediaHubItem {
  attachment: Attachment;
  sourceCategory: MediaSourceCategory;
  sourceLabel: string;
  mediaType: MediaFileType;
  mediaTypeLabel: string;
  taskTitle: string | null;
  taskStatus: Task['status'] | null;
  branchId: string | null;
  branchName: string | null;
  uploaderName: string;
}

export function resolveMediaSource(attachment: Attachment): {
  category: MediaSourceCategory;
  label: string;
} {
  const ref = attachment.clientRef ?? '';
  if (attachment.kind === 'logo' || attachment.kind === 'avatar') {
    return { category: 'branding', label: 'شعارات وهوية' };
  }
  if (ref.startsWith('chat-media:') || ref.startsWith('comment-media:')) {
    return { category: 'chat_media', label: 'وسائط الشات والتعليقات' };
  }
  if (ref.startsWith('bulletin-media:')) {
    return { category: 'bulletin_media', label: 'مرفقات التعليمات والبيانات' };
  }
  return { category: 'task_proof', label: 'إثباتات ومرفقات المهام' };
}

export function resolveMediaTypeLabel(type: MediaFileType): string {
  switch (type) {
    case 'image':
      return 'صورة';
    case 'video':
      return 'فيديو';
    case 'audio':
      return 'تسجيل صوتي';
    case 'document':
      return 'مستند / ملف';
  }
}

export async function listMediaHubItems(
  gateway: DataGateway,
  companyId: string,
  options?: { includeBranding?: boolean },
): Promise<MediaHubItem[]> {
  const [attachments, tasks, branches, profiles] = await Promise.all([
    gateway.table('attachments').list({
      filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } },
      orderBy: [{ field: 'createdAt', direction: 'desc' }],
    }),
    gateway.table('tasks').list({
      filter: { companyId: { op: 'eq', value: companyId } },
    }).catch(() => [] as Task[]),
    gateway.table('branches').list({
      filter: { companyId: { op: 'eq', value: companyId } },
    }).catch(() => [] as Branch[]),
    gateway.commands.listCompanyProfiles({ companyId }).catch(() => []),
  ]);

  const tasksById = new Map(tasks.map((t) => [t.id, t]));
  const branchesById = new Map(branches.map((b) => [b.id, b.name]));
  const namesByUserId = new Map(profiles.map((p) => [p.userId, p.fullName]));

  const items: MediaHubItem[] = [];
  for (const att of attachments) {
    if (att.uploadState !== 'uploaded' || att.deletedAt || att.storagePath.startsWith('tf-meta://')) {
      continue;
    }
    const source = resolveMediaSource(att);
    if (!options?.includeBranding && source.category === 'branding') {
      continue;
    }
    const mediaType = classifyMimeType(att.mimeType);
    const parentTask = att.taskId ? tasksById.get(att.taskId) ?? null : null;
    const branchId = parentTask?.branchId ?? null;
    const branchName = branchId ? branchesById.get(branchId) ?? null : null;

    items.push({
      attachment: att,
      sourceCategory: source.category,
      sourceLabel: source.label,
      mediaType,
      mediaTypeLabel: resolveMediaTypeLabel(mediaType),
      taskTitle: parentTask?.title ?? null,
      taskStatus: parentTask?.status ?? null,
      branchId,
      branchName,
      uploaderName: namesByUserId.get(att.uploadedByUserId) ?? 'عضو بالمنشأة',
    });
  }

  return items;
}

export function isItemEligibleForSelectiveRetention(
  item: MediaHubItem,
  policy: MediaHubRetentionPolicy,
  now: Date = new Date(),
): boolean {
  if (item.sourceCategory === 'branding') return false;

  // فحص المصدر
  if (item.sourceCategory === 'task_proof') {
    if (!policy.deleteTaskProofMedia) return false;
    if (policy.completedTasksOnly && item.taskStatus !== 'completed') return false;
  } else if (item.sourceCategory === 'chat_media') {
    if (!policy.deleteChatMedia) return false;
  } else if (item.sourceCategory === 'bulletin_media') {
    if (!policy.deleteBulletinMedia) return false;
  }

  // فحص نوع الملف
  if (item.mediaType === 'image' && !policy.deleteImages) return false;
  if (item.mediaType === 'video' && !policy.deleteVideos) return false;
  if (item.mediaType === 'audio' && !policy.deleteAudio) return false;
  if (item.mediaType === 'document' && !policy.deleteDocuments) return false;

  // فحص العمر بالأيام
  const days = Math.max(0, Number(policy.autoDeleteAfterDays) || 0);
  if (days > 0) {
    const ageMs = now.getTime() - new Date(item.attachment.createdAt).getTime();
    if (ageMs < days * 86_400_000) return false;
  }

  return true;
}

export async function runSelectiveMediaRetention(
  gateway: DataGateway,
  actor: Actor,
  policy: MediaHubRetentionPolicy,
  options: { dryRun: boolean },
): Promise<{ filesCount: number; bytesFreed: number; deletedIds: string[] }> {
  const allItems = await listMediaHubItems(gateway, actor.companyId);
  const eligible = allItems.filter((item) => isItemEligibleForSelectiveRetention(item, policy));

  if (options.dryRun || eligible.length === 0) {
    return {
      filesCount: eligible.length,
      bytesFreed: eligible.reduce((sum, item) => sum + item.attachment.sizeBytes, 0),
      deletedIds: eligible.map((item) => item.attachment.id),
    };
  }

  let filesCount = 0;
  let bytesFreed = 0;
  const deletedIds: string[] = [];

  for (const item of eligible) {
    try {
      await deleteAttachment(gateway, actor, item.attachment.id);
      filesCount += 1;
      bytesFreed += item.attachment.sizeBytes;
      deletedIds.push(item.attachment.id);
    } catch {
      // نستمر في حذف بقية الملفات المؤهلة
    }
  }

  return { filesCount, bytesFreed, deletedIds };
}

/* ============================================================================
 * 7) الإغلاق التلقائي للجولات الساعية الفائتة (Auto-Expire Missed Intraday Rounds)
 * ========================================================================== */

export async function autoExpireMissedIntradayRounds(
  gateway: DataGateway,
  actor: Actor,
  tasks: readonly Task[],
): Promise<number> {
  if (!actor.membershipId) return 0;
  const nowMs = Date.now();
  let expiredCount = 0;

  for (const task of tasks) {
    if (
      task.deletedAt ||
      !task.occurrenceKey.includes('::slot:') ||
      (task.status !== 'pending' && task.status !== 'scheduled') ||
      !task.dueAt
    ) {
      continue;
    }
    const dueMs = new Date(task.dueAt).getTime();
    // إذا انتهى وقت الجولة الساعية ومضى عليه أكثر من 5 دقائق سماح، تُغلق الجولة الفائتة تلقائيًا
    if (!Number.isNaN(dueMs) && nowMs > dueMs + 5 * 60_000) {
      try {
        await gateway.commands.cancelTask({
          taskId: task.id,
          membershipId: actor.membershipId,
          actorUserId: actor.userId,
          reason: 'إغلاق تلقائي لجولة ساعية فائتة بعد دخول وقت الجولة التالية',
        });
        expiredCount += 1;
      } catch {
        // قد لا يملك المستخدم الحالي صلاحية إلغاء هذه المهمة؛ نتجاوزها بصمت
      }
    }
  }
  return expiredCount;
}

/* ============================================================================
 * 8) تشخيص التخزين السحابي والمحلي والنسخ الاحتياطي والتصدير
 * ========================================================================== */

export interface StorageDiagnostics {
  gatewayKind: 'local' | 'supabase';
  cloudFilesCount: number;
  cloudTotalBytes: number;
  bySource: Record<MediaSourceCategory, { count: number; bytes: number }>;
  byType: Record<MediaFileType, { count: number; bytes: number }>;
  counts: {
    tasks: number;
    completedTasks: number;
    templates: number;
    branches: number;
    members: number;
    chatMessages: number;
    bulletins: number;
    handovers: number;
  };
  localQuotaBytes: number | null;
  localUsageBytes: number | null;
  localStorageBytes: number;
}

export async function getStorageDiagnostics(
  gateway: DataGateway,
  companyId: string,
): Promise<StorageDiagnostics> {
  const [items, tasks, templates, branches, memberships, chatMsgs, bulletins, handovers] =
    await Promise.all([
      listMediaHubItems(gateway, companyId, { includeBranding: true }),
      gateway.table('tasks').list({ filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } } }).catch(() => [] as Task[]),
      gateway.table('task_templates').list({ filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } } }).catch(() => []),
      gateway.table('branches').list({ filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } } }).catch(() => []),
      gateway.table('memberships').list({ filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } } }).catch(() => []),
      listChatMessages(gateway, companyId).catch(() => []),
      listBulletins(gateway, companyId).catch(() => []),
      listShiftHandovers(gateway, companyId).catch(() => []),
    ]);

  const bySource: StorageDiagnostics['bySource'] = {
    task_proof: { count: 0, bytes: 0 },
    chat_media: { count: 0, bytes: 0 },
    bulletin_media: { count: 0, bytes: 0 },
    branding: { count: 0, bytes: 0 },
  };
  const byType: StorageDiagnostics['byType'] = {
    image: { count: 0, bytes: 0 },
    video: { count: 0, bytes: 0 },
    audio: { count: 0, bytes: 0 },
    document: { count: 0, bytes: 0 },
  };

  let cloudTotalBytes = 0;
  for (const item of items) {
    const b = item.attachment.sizeBytes || 0;
    cloudTotalBytes += b;
    bySource[item.sourceCategory].count += 1;
    bySource[item.sourceCategory].bytes += b;
    byType[item.mediaType].count += 1;
    byType[item.mediaType].bytes += b;
  }

  let localQuotaBytes: number | null = null;
  let localUsageBytes: number | null = null;
  if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
    try {
      const est = await navigator.storage.estimate();
      localQuotaBytes = est.quota ?? null;
      localUsageBytes = est.usage ?? null;
    } catch {
      // تجاهل
    }
  }

  let localStorageBytes = 0;
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i) ?? '';
      const v = localStorage.getItem(k) ?? '';
      localStorageBytes += (k.length + v.length) * 2;
    }
  } catch {
    // تجاهل
  }

  return {
    gatewayKind: gateway.kind,
    cloudFilesCount: items.length,
    cloudTotalBytes,
    bySource,
    byType,
    counts: {
      tasks: tasks.length,
      completedTasks: tasks.filter((t) => t.status === 'completed').length,
      templates: templates.length,
      branches: branches.length,
      members: memberships.length,
      chatMessages: chatMsgs.length,
      bulletins: bulletins.length,
      handovers: handovers.length,
    },
    localQuotaBytes,
    localUsageBytes,
    localStorageBytes,
  };
}

export async function exportCompanyBackupJson(
  gateway: DataGateway,
  companyId: string,
  companyName: string,
): Promise<void> {
  const [
    company,
    brands,
    branches,
    categories,
    memberships,
    roles,
    templates,
    tasks,
    settings,
    bulletins,
    handovers,
    chatMessages,
  ] = await Promise.all([
    gateway.table('companies').get(companyId).catch(() => null),
    gateway.table('brands').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('branches').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('task_categories').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('memberships').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('roles').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('task_templates').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('tasks').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('settings').list({ filter: { scopeId: { op: 'eq', value: companyId } } }).catch(() => []),
    listBulletins(gateway, companyId).catch(() => []),
    listShiftHandovers(gateway, companyId).catch(() => []),
    listChatMessages(gateway, companyId).catch(() => []),
  ]);

  const backupPayload = {
    schemaVersion: '2.0',
    exportedAt: new Date().toISOString(),
    companyName,
    companyId,
    data: {
      company,
      brands,
      branches,
      categories,
      memberships,
      roles,
      templates,
      tasks,
      settings,
      bulletins,
      shiftHandovers: handovers,
      chatMessages,
    },
  };

  const blob = new Blob([JSON.stringify(backupPayload, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const datePart = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `taskflow-backup-${company?.slug ?? 'company'}-${datePart}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function exportTasksCsv(
  gateway: DataGateway,
  companyId: string,
  companySlug = 'company',
): Promise<void> {
  const [tasks, branches, categories] = await Promise.all([
    gateway.table('tasks').list({
      filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } },
      orderBy: [{ field: 'createdAt', direction: 'desc' }],
    }),
    gateway.table('branches').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('task_categories').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
  ]);

  const branchMap = new Map(branches.map((b) => [b.id, b.name]));
  const categoryMap = new Map(categories.map((c) => [c.id, c.name]));

  const headers = [
    'معرف المهمة',
    'العنوان',
    'الفرع',
    'الفئة',
    'الحالة',
    'حالة الاعتماد',
    'الأولوية',
    'التاريخ المحلي',
    'وقت البدء',
    'الموعد النهائي',
    'تاريخ الإنجاز',
  ];

  const escapeCsv = (val: string | null | undefined) => {
    const str = String(val ?? '').replace(/"/g, '""');
    return `"${str}"`;
  };

  const lines = [
    headers.map(escapeCsv).join(','),
    ...tasks.map((t) =>
      [
        t.id,
        t.title,
        t.branchId ? branchMap.get(t.branchId) ?? t.branchId : 'عام',
        categoryMap.get(t.categoryId) ?? t.categoryId,
        t.status,
        t.approvalState,
        t.priority,
        t.localDate,
        t.startAt,
        t.dueAt ?? '',
        t.completedAt ?? '',
      ]
        .map(escapeCsv)
        .join(','),
    ),
  ];

  // BOM لدعم العربية في Excel
  const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const datePart = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `taskflow-tasks-${companySlug}-${datePart}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
